import { endInX9, randomRoundUpCents } from '@/utils/price-transforms';
import {
  MICRO_CONVERSION,
  ONE_CENT,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { PricingCalculations } from '@/utils/pricing-calculations';

export interface OverridePricing {
  grossProfitInPercentage: number;
  priceInMicros: number;
}

const snapToWholeCent = (
  priceInMicros: number,
  snapshotCostInMicros: number,
): number => {
  const rounded = roundMicrosToNearestWholeCent(priceInMicros);
  // Ceil instead of round-to-nearest when nearest would land below cost,
  // so the snapped price stays at-or-above the floor.
  if (rounded < snapshotCostInMicros) {
    return Math.ceil(priceInMicros / ONE_CENT) * ONE_CENT;
  }
  return rounded;
};

const fromPriceMicros = (
  priceInMicros: number,
  snapshotCostInMicros: number,
): OverridePricing | null => {
  const snapped = snapToWholeCent(priceInMicros, snapshotCostInMicros);
  const grossProfitInPercentage =
    PricingCalculations.grossProfitPercentFromPrice(
      snapped,
      snapshotCostInMicros,
    );
  if (grossProfitInPercentage === null) {
    return null;
  }
  return { grossProfitInPercentage, priceInMicros: snapped };
};

export const recommendationPricing = {
  adjustGrossProfit: (
    currentGrossProfitInPercentage: number,
    delta: number,
    snapshotCostInMicros: number,
  ): OverridePricing | null => {
    const next = currentGrossProfitInPercentage + delta;
    if (next < 0 || next >= 100) {
      return null;
    }
    return recommendationPricing.fromGrossProfitPercent(
      next,
      snapshotCostInMicros,
    );
  },

  endInXNine: (
    priceInMicros: number,
    snapshotCostInMicros: number,
  ): OverridePricing | null => {
    const newPriceInMicros = endInX9(priceInMicros);
    if (newPriceInMicros === null) {
      return null;
    }
    return fromPriceMicros(newPriceInMicros, snapshotCostInMicros);
  },

  fromGrossProfitPercent: (
    grossProfitPercent: number,
    snapshotCostInMicros: number,
  ): OverridePricing | null => {
    const priceInMicros = PricingCalculations.priceFromGrossProfitPercent(
      grossProfitPercent,
      snapshotCostInMicros,
    );
    if (priceInMicros === null) {
      return null;
    }
    // Keep the user-entered GM% verbatim; recomputing it from the snapped
    // price would drift the value they just typed.
    return {
      grossProfitInPercentage: grossProfitPercent,
      priceInMicros: snapToWholeCent(priceInMicros, snapshotCostInMicros),
    };
  },

  fromPriceUnits: (
    priceInUnits: number,
    snapshotCostInMicros: number,
  ): OverridePricing | null => {
    const priceInMicros = Math.round(priceInUnits * MICRO_CONVERSION);
    return fromPriceMicros(priceInMicros, snapshotCostInMicros);
  },

  randomRoundUp: (
    priceInMicros: number,
    snapshotCostInMicros: number,
  ): OverridePricing | null => {
    const newPriceInMicros = randomRoundUpCents(priceInMicros);
    if (newPriceInMicros === null) {
      return null;
    }
    return fromPriceMicros(newPriceInMicros, snapshotCostInMicros);
  },
};
